import React, { createContext, useEffect, useRef, useState } from 'react'

import "react-datepicker/dist/react-datepicker.css";
import { TimesheetHeader } from "./components/timesheetHeader/TimesheetHeader";
import { TimesheetTable } from "./components/timesheetTable/TimesheetTable";
import { TimesheetStyled } from "./Timesheet.styled";
import { IComment, IStatistic, ISummaryEmployee, ITask, ITimesheet, IUser } from "../../models";
import axios from "axios";
import { getApiLink } from "../../functions/getApiLink";
import { useDispatch, useSelector } from 'react-redux';
import { setChosenTimesheet, setTasks, setExpenses } from '../../storage/toolkit';
import { CustomSelect } from "../../components/customSelect/CustomSelect";
import { RowsPerPage } from "../../constants/RowsPerPage";
import { DownSidebar } from "../../components/downSidebar/DownSidebar";

import { useParams } from "react-router-dom";
import { Translate } from "../../components/translate/Translate";
import { SetStatistic } from "../../api/SetStatistic";
import { SetTimesheet } from "../../api/SetTimesheet";
import { SetTasks } from "../../api/SetTasks";

// Імпортуємо axiosConfig для інтерсепторів
import "../../api/axiosConfig";

interface ITimesheetProps {

}

export const BlockToEdit: any = createContext(null)
export const BlockToDuplicate: any = createContext(null)
export const FixedTopEdit: any = createContext(null)
export const AmountStatistic: any = createContext(null)

export const Timesheet: React.FC<ITimesheetProps> = () => {

    const dispatch = useDispatch()

    const { timesheetId }: any = useParams()

    const user: IUser = useSelector((state: any) => state.toolkit.user)
    const taskList: ITask[] = useSelector((state: any) => state.toolkit.tasks)
    const chosenTimesheet: ITimesheet = useSelector((state: any) => state.toolkit.chosenTimesheet)
    const timesheetStatistic: IStatistic = useSelector((state: any) => state.toolkit.timesheetStatistic)
    const summaryEmployees: ISummaryEmployee = useSelector((state: any) => state.toolkit.summaryEmployees)
    const timesheet: ITimesheet[] = useSelector(
        (state: any) => state.toolkit.timesheet
      );

    const [rowsSelectValue, setRowsSelectValue] = useState(RowsPerPage()[0])
    const [itemToEdit, setItemToEdit] = useState<ITask>()
    const [itemToDuplicate, setItemToDuplicate] = useState<ITask>()
    const [isOpenDownSidebar, setIsOpenDownSidebar] = useState(false)
    const [statistic, setStatistic] = useState<IStatistic | undefined>()
    const [isLoad, setIsLoad] = useState(false)
    const [isWindowMobile, setIsWindowMobile] = useState(false)
    const [comments, setComments] = useState<IComment[]>([])
    
    // Loading state to prevent flicker
    const [isLoading, setIsLoading] = useState(true);
    const mountedRef = useRef(true);
    const lastTimesheetIdRef = useRef<string | undefined>(undefined);
    const chosenTimesheetSetRef = useRef<string | undefined>(undefined);
    const abortControllerRef = useRef<AbortController | null>(null);

    // MAIN EFFECT: Handle page load and navigation
    useEffect(() => {
        mountedRef.current = true;
        
        // Cancel any pending requests
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        
        const loadData = async () => {
            setIsLoading(true);
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth() + 1;
            
            if (timesheetId) {
                // VIEWING SPECIFIC TIMESHEET (e.g., from Summary)
                window.scrollTo(0, 0);
                
                try {
                    const [timesheetRes, tasksRes, statsRes] = await Promise.all([
                        axios.get(getApiLink(`/api/timesheet/${timesheetId}/`)),
                        axios.get(getApiLink(`/api/timesheet/tasks/?timesheet_id=${timesheetId}`)),
                        axios.get(getApiLink(`/api/timesheet/statistics/?timesheet_id=${timesheetId}`))
                    ]);
                    
                    if (!mountedRef.current) return;
                    
                    chosenTimesheetSetRef.current = timesheetRes.data?.id;
                    dispatch(setChosenTimesheet(timesheetRes.data));
                    dispatch(setTasks(tasksRes.data));
                    setStatistic(statsRes.data);
                    SetStatistic(dispatch, timesheetId);
                } catch (err) {
                    console.error("Error loading specific timesheet:", err);
                }
            } else {
                // MAIN PAGE - load current user's timesheet
                // Clear previous data first to avoid showing stale data
                dispatch(setTasks([]));
                dispatch(setExpenses([]));
                chosenTimesheetSetRef.current = undefined;
                
                // Load timesheet list for current year
                await SetTimesheet(dispatch, currentYear);
            }
            
            if (mountedRef.current) {
                setIsLoading(false);
            }
        };
        
        // Always reload when timesheetId changes
        if (lastTimesheetIdRef.current !== timesheetId) {
            lastTimesheetIdRef.current = timesheetId;
            chosenTimesheetSetRef.current = undefined;
            loadData();
        }
        
        return () => {
            mountedRef.current = false;
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [timesheetId, dispatch]);

    // When timesheet list loads on main page, select appropriate month
    useEffect(() => {
        if (timesheetId) return; // Don't run for specific timesheet
        if (!user?.id) return;
        
        // If timesheet list is empty (no data for selected year), clear everything
        if (!timesheet?.length) {
            if (chosenTimesheetSetRef.current) {
                chosenTimesheetSetRef.current = undefined;
                dispatch(setChosenTimesheet({}));
                dispatch(setTasks([]));
                dispatch(setExpenses([]));
            }
            return;
        }
        
        // CRITICAL: If current chosenTimesheet belongs to another user, ignore it
        if (chosenTimesheet?.user?.id && chosenTimesheet.user.id !== user.id) {
            chosenTimesheetSetRef.current = undefined;
        }
        
        // Try to find timesheet for the month stored in cookie (user's selection),
        // fall back to current month, then to first available
        const cookieMonth = document.cookie.match(/timesheetMonth=(\d+)/);
        const selectedMonth = cookieMonth ? Number(cookieMonth[1]) : (new Date().getMonth() + 1);
        
        const matching = timesheet.find((item) => {
            const monthFromDate = Number(item.date.split("/")[1]);
            return monthFromDate === selectedMonth;
        });
        
        const selectedTimesheet = matching || timesheet[0];
        
        // Only update if different from what we've already set (use ref to prevent loops)
        if (selectedTimesheet?.id && chosenTimesheetSetRef.current !== selectedTimesheet.id) {
            chosenTimesheetSetRef.current = selectedTimesheet.id;
            dispatch(setChosenTimesheet(selectedTimesheet));
        }
    }, [timesheet, timesheetId, user?.id, dispatch, chosenTimesheet?.user?.id]);

    // Load tasks when chosenTimesheet changes on main page
    useEffect(() => {
        if (timesheetId) return; // Skip for specific timesheet (already loaded)
        if (!chosenTimesheet?.id) return;
        if (!user?.id) return;
        
        // CRITICAL: Verify it's current user's timesheet (not someone else's from Summary)
        if (chosenTimesheet.user?.id && chosenTimesheet.user.id !== user.id) {
            // This is someone else's timesheet - clear it and reload own data
            console.log("[Timesheet] Detected foreign timesheet, clearing...");
            dispatch(setChosenTimesheet({}));
            dispatch(setTasks([]));
            return;
        }
        
        // Cancel previous request if any
        const controller = new AbortController();
        
        const loadTasks = async () => {
            // Clear tasks before loading new ones to prevent overlay
            dispatch(setTasks([]));
            
            try {
                const [tasksRes, statsRes] = await Promise.all([
                    axios.get(getApiLink(`/api/timesheet/tasks/?timesheet_id=${chosenTimesheet.id}`), { signal: controller.signal }),
                    axios.get(getApiLink(`/api/timesheet/statistics/?timesheet_id=${chosenTimesheet.id}`), { signal: controller.signal })
                ]);
                
                if (controller.signal.aborted) return;
                
                dispatch(setTasks(tasksRes.data));
                setStatistic(statsRes.data);
                SetStatistic(dispatch, chosenTimesheet.id);
            } catch (err: any) {
                if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
                console.error("Error loading tasks:", err);
            }
        };
        
        loadTasks();
        
        return () => {
            controller.abort();
        };
    }, [chosenTimesheet?.id, timesheetId, user?.id, dispatch]);

    useEffect(() => {
        if (summaryEmployees?.all?.length && timesheetId) {
            const found = summaryEmployees.all.find(item => item.id === timesheetId) 
                       || summaryEmployees.favourite?.find(item => item.id === timesheetId);
            
            // Use ref to prevent loops - only dispatch if we haven't set this id yet
            if (found && chosenTimesheetSetRef.current !== found.id) {
                chosenTimesheetSetRef.current = found.id;
                dispatch(setChosenTimesheet(found));
            }
        }
    }, [summaryEmployees, timesheetId, dispatch]);

    useEffect(() => {
        setIsWindowMobile(window.innerWidth < 576)
    }, [])

    useEffect(() => {
        if (isLoad) return;

        setRowsSelectValue(taskList.length > +RowsPerPage()[0].value ? RowsPerPage()[0] : RowsPerPage()[3])
        setTimeout(() => {
            setIsLoad(true)
        }, 1000)
    }, [taskList, isLoad])

    useEffect(() => {
        if (!timesheetStatistic) return;
        setStatistic(timesheetStatistic)
    }, [timesheetStatistic])

    const handleAddRows = () => {
        const plusCount = window.innerWidth < 768 ? 10 : 20

        setRowsSelectValue({
            value: taskList.length <= +rowsSelectValue.label + plusCount ? 0 : rowsSelectValue.value + plusCount,
            label: taskList.length <= +rowsSelectValue.label + plusCount ? "All" : String(+rowsSelectValue.label + plusCount)
        })
    }


    useEffect(() => {
        if (!chosenTimesheet?.id) return;

        axios.get(getApiLink(`/api/comment/?timesheet_id=${chosenTimesheet.id}`)).then(({ data }) => {
            setComments(data)
        })

    }, [chosenTimesheet])

    const [isFixedEditBlock, setIsFixedEditBlock] = useState(false)
    const [amountStatistic, setAmountStatistic] = useState(0)

    return (
        <BlockToDuplicate.Provider value={setItemToDuplicate}>
            <AmountStatistic.Provider value={setAmountStatistic}>
                <FixedTopEdit.Provider value={setIsFixedEditBlock}>
                    <BlockToEdit.Provider value={setItemToEdit}>
                        <TimesheetStyled style={{ paddingBottom: isOpenDownSidebar ? "270px" : (isWindowMobile ? "60px" : "80px") }}
                            className={`section-table ${isFixedEditBlock && "fixed-edit-block"}`}>

                            <TimesheetHeader itemToDuplicate={itemToDuplicate} setItemToDuplicate={setItemToDuplicate} isFixedEditBlock={isFixedEditBlock} itemToEdit={itemToEdit} />

                            {chosenTimesheet?.id && <TimesheetTable itemToEdit={itemToEdit} rowsSelectValue={rowsSelectValue} />}

                            <div className="section-table__footer">
                                <div className="section-table__row-per-page visible-on-mob">
                                    <span>
                                        <Translate>timesheet_page.table.rows_per_page</Translate>
                                    </span>

                                    <CustomSelect list={RowsPerPage()} defaultValue={RowsPerPage()[3]}
                                        selectValue={rowsSelectValue}
                                        setSelectedItem={setRowsSelectValue} />
                                </div>
                                {rowsSelectValue.value !== 0 && taskList.length > rowsSelectValue.value &&
                                    <button onClick={handleAddRows} className="section-table__see-more btn"
                                        type="button">
                                        <Translate>timesheet_page.table.show_more</Translate>
                                        <svg width="15" height="15" viewBox="0 0 15 15">
                                            <use xlinkHref="#arrow-down"></use>
                                        </svg>
                                    </button>}
                                <div className="section-table__row-per-page visible-on-desktop">
                                    <span>
                                        <Translate>timesheet_page.table.rows_per_page</Translate>
                                    </span>

                                    <CustomSelect list={RowsPerPage()} defaultValue={RowsPerPage()[3]}
                                        selectValue={rowsSelectValue}
                                        setSelectedItem={setRowsSelectValue} />
                                </div>
                            </div>
                        </TimesheetStyled>

                        <DownSidebar comments={comments}
                            amountStatistic={amountStatistic}
                            setComments={setComments}
                            type={"timesheet"}
                            statisticAllAmount={statistic?.all_hours ?? timesheetStatistic.all_hours}
                            statisticAllElements={statistic?.tasks ?? timesheetStatistic.tasks}
                            setIsOpenDownSidebar={setIsOpenDownSidebar} />
                    </BlockToEdit.Provider>
                </FixedTopEdit.Provider>
            </AmountStatistic.Provider>
        </BlockToDuplicate.Provider>
    )
}
